import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@mui/material';
import { FormatColorText } from '@mui/icons-material';
import { useSlate } from 'slate-react';
import { Editor, Transforms, Text } from 'slate';
import { ColorPicker, useColor } from 'react-color-palette';
import 'react-color-palette/css';

const setColorMark = (editor, colorHex) => {
    Transforms.setNodes(editor, {}, { match: n => Text.isText(n), split: true });
    Editor.addMark(editor, "color", colorHex);
};

const removeColorMark = (editor) => {
    Editor.removeMark(editor, "color");
};

const ColorPickerButton = () => {
    const editor = useSlate();
    const [showPicker, setShowPicker] = useState(false);
    const [color, setColor] = useColor("hex", "#000000");
    const buttonRef = useRef(null);
    const pickerRef = useRef(null);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

    const onChange = (newColor) => {
        setColor(newColor);
        newColor.hex ? setColorMark(editor, newColor.hex) : removeColorMark(editor);
    };

    // Update picker position when opened
    useEffect(() => {
        if (showPicker && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPickerPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
            });
        }
    }, [showPicker]);

    // Close picker on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setShowPicker(false);
            }
        };

        if (showPicker) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPicker]);

    return (
        <div>
            <Tooltip title="Text color">
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => setShowPicker(prev => !prev)}
                    className="text-[#cccccc] text-lg text-center justify-center flex"
                    aria-label="Text color"
                >
                    <FormatColorText fontSize="inherit" />
                </button>
            </Tooltip>

            {showPicker &&
                createPortal(
                    <div
                        ref={pickerRef}
                        style={{
                            position: 'absolute',
                            top: pickerPosition.top,
                            left: pickerPosition.left,
                            zIndex: 9999,
                            background: '#fff',
                            padding: '4px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        }}
                    >
                        <ColorPicker width={200} height={150} color={color} onChange={onChange} />
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default ColorPickerButton;
